import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ChevronsUpDown, FlaskConical, Loader2, Plus, X } from "lucide-react";
import { BETA_FEATURE_FLAG_KEYS } from "@shared/schema";

type BetaFlag = { key: string; name: string; description: string };
type BetaGrant = {
  id: string;
  userId: string;
  flagKey: string;
  createdAt: string | null;
  userEmail: string | null;
  userName: string | null;
};
type AdminUser = {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  isMaster: boolean;
  deletedAt?: string | null;
};

// Reusable manager UI — rendered on the standalone /admin/beta-features page
// AND embedded as the "Beta Features" tab on the admin dashboard.
export function BetaFeaturesManager() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedFlagKeys, setSelectedFlagKeys] = useState<string[]>([]);
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState<{
    userLabel: string;
    featureName: string;
    grantIds: string[];
  } | null>(null);

  const { data, isLoading } = useQuery<{
    flags: BetaFlag[];
    grants: BetaGrant[];
  }>({
    queryKey: ["/api/admin/beta-features"],
  });
  const {
    data: users = [], isPending: usersLoading, isError: usersFailed, refetch: reloadUsers,
  } = useQuery<AdminUser[]>({
    // Do not reuse a failed or empty dashboard cache from another session.
    queryKey: ["/api/admin/users", user?.id, "beta-feature-picker"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/users");
      const result = await response.json();
      if (!Array.isArray(result)) throw new Error("Could not load users");
      return result;
    },
    enabled: Boolean(user?.isMaster),
    staleTime: 30_000,
    refetchOnMount: "always",
    retry: (failures, error) => failures < 2 && !error.message.startsWith("403:"),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/admin/beta-features"] });
    // Live-toggle: refresh mounted beta-flag queries so gated UI updates
    // without a reload. Query keys exist in both joined and segmented forms.
    queryClient.invalidateQueries({
      predicate: (q) =>
        q.queryKey.some(
          (part) =>
            typeof part === "string" &&
            (part === "beta-features" || part.endsWith("/beta-features")),
        ),
    });
  };

  const grantMutation = useMutation({
    mutationFn: async () =>
      apiRequest("POST", "/api/admin/beta-features/grants", {
        userIds: selectedUserIds,
        flagKeys: selectedFlagKeys,
      }),
    onSuccess: () => {
      invalidate();
      const nFlags = selectedFlagKeys.length;
      const nUsers = selectedUserIds.length;
      setSelectedFlagKeys([]);
      toast({
        title: nFlags > 1 ? "Beta features granted" : "Beta feature granted",
        description:
          nFlags > 1 || nUsers > 1
            ? `Enabled ${nFlags} feature${nFlags > 1 ? "s" : ""} for ${nUsers} user${nUsers > 1 ? "s" : ""}.`
            : undefined,
      });
    },
    onError: (e: any) => {
      toast({ title: "Failed to grant", description: e?.message, variant: "destructive" });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await apiRequest("DELETE", `/api/admin/beta-features/grants/${id}`);
      }
    },
    onSuccess: () => {
      invalidate();
      setPendingRevoke(null);
      toast({ title: "Beta feature revoked" });
    },
    onError: (e: any) => {
      invalidate();
      toast({ title: "Failed to revoke", description: e?.message, variant: "destructive" });
    },
  });

  const flags = (data?.flags ?? []).filter((flag) => BETA_FEATURE_FLAG_KEYS.includes(flag.key));
  const grants = (data?.grants ?? []).filter((grant) => BETA_FEATURE_FLAG_KEYS.includes(grant.flagKey));
  const userLabel = (u: AdminUser) =>
    [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email || u.id;
  // Deactivated accounts remain visible elsewhere in Admin but cannot receive
  // new feature grants here.
  const eligibleUsers = users.filter((u) => !u.deletedAt);
  const toggleUser = (id: string) =>
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((u) => u !== id) : [...prev, id],
    );
  const toggleFlag = (key: string) =>
    setSelectedFlagKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  // Group grants per user and feature for a concise view.
  const groupedByUser = (() => {
    const byUser = new Map<
      string,
      {
        userId: string;
        userName: string | null;
        userEmail: string | null;
        features: Map<string, { flagKey: string; grants: BetaGrant[] }>;
      }
    >();
    for (const g of grants) {
      let u = byUser.get(g.userId);
      if (!u) {
        u = { userId: g.userId, userName: g.userName, userEmail: g.userEmail, features: new Map() };
        byUser.set(g.userId, u);
      }
      let f = u.features.get(g.flagKey);
      if (!f) {
        f = { flagKey: g.flagKey, grants: [] };
        u.features.set(g.flagKey, f);
      }
      f.grants.push(g);
    }
    return Array.from(byUser.values()).sort((a, b) =>
      (a.userName || a.userEmail || a.userId).localeCompare(b.userName || b.userEmail || b.userId),
    );
  })();
  const flagName = (key: string) => flags.find((f) => f.key === key)?.name || key;

  const userPickerLabel =
    selectedUserIds.length === 0
      ? "Select users…"
      : selectedUserIds.length === 1
        ? (() => {
            const u = eligibleUsers.find((candidate) => candidate.id === selectedUserIds[0]);
            return u ? userLabel(u) : "1 user";
          })()
        : `${selectedUserIds.length} users selected`;

  return (
    <div className="space-y-6">
      <Card data-testid="card-grant-form">
        <CardHeader>
          <CardTitle className="text-base">Grant beta features</CardTitle>
          <CardDescription>
            Pick one or more users and features. Every selected feature is granted to every
            selected user in a single step.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[220px]">
              <p className="text-xs font-medium mb-1">Users</p>
              <Popover open={userPickerOpen} onOpenChange={setUserPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={userPickerOpen}
                    disabled={usersLoading || usersFailed}
                    className="w-full justify-between font-normal"
                    data-testid="button-select-users"
                  >
                    <span className={selectedUserIds.length === 0 ? "text-muted-foreground" : ""}>
                      {usersLoading ? "Loading users..." : userPickerLabel}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-0" align="start">
                  <div className="flex items-center justify-between px-3 py-2 border-b">
                    <p className="text-xs font-medium text-muted-foreground">
                      {selectedUserIds.length} of {eligibleUsers.length} selected
                    </p>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => setSelectedUserIds(eligibleUsers.map((u) => u.id))}
                        data-testid="button-select-all-users"
                      >
                        All
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => setSelectedUserIds([])}
                        data-testid="button-clear-users"
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto p-1">
                    {eligibleUsers.length === 0 && (
                      <p className="px-2 py-3 text-sm text-muted-foreground">No active users found.</p>
                    )}
                    {eligibleUsers.map((u) => (
                      <label
                        key={u.id}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm cursor-pointer hover:bg-muted/60"
                        data-testid={`option-user-${u.id}`}
                      >
                        <Checkbox
                          checked={selectedUserIds.includes(u.id)}
                          onCheckedChange={() => toggleUser(u.id)}
                          data-testid={`checkbox-user-${u.id}`}
                        />
                        <span className="min-w-0">
                          <span className="block truncate">{userLabel(u)}</span>
                          {u.email && userLabel(u) !== u.email && (
                            <span className="block truncate text-xs text-muted-foreground">{u.email}</span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              {usersLoading && <p role="status" className="mt-2 text-xs text-muted-foreground">Loading users...</p>}
              {usersFailed && (
                <div role="alert" className="mt-2 space-y-1">
                  <p className="text-xs text-destructive">Could not load users.</p>
                  <Button variant="outline" size="sm" onClick={() => void reloadUsers()} data-testid="button-retry-users">
                    Retry
                  </Button>
                </div>
              )}
            </div>
            <Button
              onClick={() => grantMutation.mutate()}
              disabled={
                selectedUserIds.length === 0 ||
                selectedFlagKeys.length === 0 ||
                grantMutation.isPending || usersLoading || usersFailed
              }
              data-testid="button-grant-flag"
            >
              {grantMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-1.5" />
              )}
              Grant
              {selectedFlagKeys.length > 1 ? ` ${selectedFlagKeys.length} features` : ""}
              {selectedUserIds.length > 1 ? ` for ${selectedUserIds.length} users` : ""}
            </Button>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium">Features</p>
              {flags.length > 1 && (
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => setSelectedFlagKeys(flags.map((f) => f.key))}
                    data-testid="button-select-all-flags"
                  >
                    All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => setSelectedFlagKeys([])}
                    data-testid="button-clear-flags"
                  >
                    Clear
                  </Button>
                </div>
              )}
            </div>
            <div className="rounded-md border divide-y">
              {flags.map((flag) => (
                <label
                  key={flag.key}
                  className="flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/40"
                  data-testid={`option-flag-${flag.key}`}
                >
                  <Checkbox
                    checked={selectedFlagKeys.includes(flag.key)}
                    onCheckedChange={() => toggleFlag(flag.key)}
                    className="mt-0.5"
                    data-testid={`checkbox-flag-${flag.key}`}
                  />
                  <span className="min-w-0">
                    <span className="flex items-center gap-2 text-sm font-medium">
                      {flag.name}
                      <Badge variant="secondary" className="font-mono text-[10px]">{flag.key}</Badge>
                    </span>
                    <span className="block text-xs text-muted-foreground">{flag.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-current-grants">
        <CardHeader>
          <CardTitle className="text-base">Current grants</CardTitle>
          <CardDescription>Everyone who has a beta feature turned on right now.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading grants…
            </div>
          ) : grants.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="text-no-grants">
              No one has a beta feature yet.
            </p>
          ) : (
            <div className="divide-y">
              {groupedByUser.map((u) => (
                <div key={u.userId} className="py-3 first:pt-0 last:pb-0" data-testid={`row-user-grants-${u.userId}`}>
                  <p className="text-sm font-medium" data-testid={`text-grant-user-${u.userId}`}>
                    {u.userName || u.userEmail || u.userId}
                    {u.userName && u.userEmail && (
                      <span className="text-muted-foreground text-xs font-normal ml-1.5">{u.userEmail}</span>
                    )}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {Array.from(u.features.values()).map((f) => {
                      return (
                        <Badge
                          key={f.flagKey}
                          variant="secondary"
                          className="gap-1.5 pr-1 font-normal"
                          data-testid={`badge-feature-${u.userId}-${f.flagKey}`}
                        >
                          <span>{flagName(f.flagKey)}</span>
                          <button
                            type="button"
                            className="rounded-sm hover:bg-muted-foreground/20 p-0.5"
                            onClick={() =>
                              setPendingRevoke({
                                userLabel: u.userName || u.userEmail || u.userId,
                                featureName: flagName(f.flagKey),
                                grantIds: f.grants.map((g) => g.id),
                              })
                            }
                            disabled={revokeMutation.isPending}
                            aria-label={`Revoke ${flagName(f.flagKey)}`}
                            data-testid={`button-revoke-${u.userId}-${f.flagKey}`}
                          >
                            <X className="h-3 w-3 text-destructive" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!pendingRevoke} onOpenChange={(open) => !open && setPendingRevoke(null)}>
        <AlertDialogContent data-testid="dialog-confirm-revoke">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke beta feature?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRevoke &&
                `This turns off "${pendingRevoke.featureName}" for ${pendingRevoke.userLabel}.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-revoke">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pendingRevoke && revokeMutation.mutate(pendingRevoke.grantIds)}
              disabled={revokeMutation.isPending}
              data-testid="button-confirm-revoke"
            >
              {revokeMutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function AdminBetaFeaturesPage() {
  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <FlaskConical className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold" data-testid="text-page-title">Beta Features</h1>
          <p className="text-sm text-muted-foreground">
            Grant registry beta features directly to users. Features are off until a user receives
            a grant, and apply anywhere that user can access the gated experience.
          </p>
        </div>
      </div>
      <BetaFeaturesManager />
    </div>
  );
}
